import axios from 'axios'

const API_BASE = '/api/analyses'

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('user_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Shared axios instance for AI endpoints with auth
const aiApi = axios.create({
  headers: { 'Content-Type': 'application/json' },
})
aiApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('user_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export interface SemanticParams {
  search_id: string
  text_source?: 'abstract' | 'title' | 'mesh' | 'keywords' | 'both'
  n_topics?: number
  n_terms?: number
  doc_map_method?: 'umap' | 'lsa'
  umap_neighbors?: number
  umap_min_dist?: number
  lsa_dimensions?: number
}

export interface AnalysisResult {
  analysis_id: string
  status: string
  results?: SemanticResults
  error_message?: string
}

export interface SemanticResults {
  config: {
    text_source: string
    n_topics: number
    n_terms: number
    doc_map_method: string
    umap_neighbors: number
    umap_min_dist: number
    lsa_dimensions: number
  }
  n_documents: number
  vocabulary_size: number
  topic_summary: Record<string, TopicTerm[]>
  topic_distribution: TopicDistribution[]
  documents: DocumentInfo[]
  topic_top_terms: TopicTerm[]
  vocabulary: string[]
  nmf?: {
    n_topics: number
    reconstruction_err: number
    n_iter: number
    top_terms: any[]
  }
  lsa?: {
    u: number[][]
    d: number[]
    v: number[][]
    terms: string[]
    docs: number[]
    explained_variance: number[]
  }
}

export interface TopicTerm {
  topic: number
  term: string
  beta: number
  rank: number
}

export interface TopicDistribution {
  topic: number
  n_docs: number
  avg_gamma: number
}

export interface DocumentInfo {
  doc_id: number
  pmid: string
  title: string | null
  authors: string[] | null
  journal: string | null
  year: number | null
  quartile: string | null
  dominant_topic: number
  topic_prob: number
  umap_x?: number
  umap_y?: number
  lsa_x?: number
  lsa_y?: number
}

export interface BERTopicParams {
  search_id: string
  text_source?: 'abstract' | 'title' | 'mesh' | 'keywords' | 'both'
  n_topics?: number
  min_topic_size?: number
  embedding_model?: string
}

export interface BERTopicResults {
  n_documents: number
  n_topics: number
  topic_summary: Record<string, { term: string; score: number }[]>
  topic_distribution: { topic: number; n_docs: number }[]
  documents: BERTopicDocument[]
  representative_docs: Record<string, { doc_id: number; pmid: string; title: string | null; prob: number }[]>
  model_info: {
    embedding_model: string
    has_probabilities: boolean
  }
}

export interface BERTopicDocument {
  doc_id: number
  pmid: string
  title: string | null
  authors: string[] | null
  journal: string | null
  year: number | null
  quartile: string | null
  topic: number
  topic_prob: number
  text: string
  umap_x?: number
  umap_y?: number
}

export async function startSemanticAnalysis(
  name: string,
  params: SemanticParams
): Promise<{ analysis_id: string; status: string }> {
  const response = await api.post('/semantic', {
    name,
    analysis_type: 'semantic',
    params,
  })
  return response.data
}

export async function startBERTopicAnalysis(
  name: string,
  params: BERTopicParams
): Promise<{ analysis_id: string; status: string }> {
  const response = await api.post('/bertopic', {
    name,
    analysis_type: 'bertopic',
    params,
  })
  return response.data
}

export async function getAnalysis(analysisId: string): Promise<AnalysisResult> {
  const response = await api.get(`/${analysisId}`)
  return response.data
}

export async function getAnalysisTopics(analysisId: string) {
  const response = await api.get(`/${analysisId}/topics`)
  return response.data
}

export async function getAnalysisHeatmap(analysisId: string) {
  const response = await api.get(`/${analysisId}/heatmap`)
  return response.data
}

export async function getTopicInsights(
  topicSummary: Record<string, any[]>,
  analysisType: 'semantic' | 'bertopic',
  nDocuments: number
): Promise<{ insights: string; n_topics: number; analysis_type: string }> {
  const response = await aiApi.post('/api/ai/topic-insights', {
    topic_summary: topicSummary,
    analysis_type: analysisType,
    n_documents: nDocuments,
  }, {
    timeout: 300000, // 5 minutes - AI insights can take a while
  })
  return response.data
}

export interface TableColumnTemplate {
  name: string
  instruction: string
}

export interface BatchAnalysisParams {
  search_id: string
  question: string
  output_format: 'text' | 'table'
  max_rows: number
  row_selection: 'top' | 'random'
  temperature: number
  detail_level: 'normal' | 'detailed'
  table_template?: TableColumnTemplate[]
}

export async function startBatchAnalysis(
  params: BatchAnalysisParams
): Promise<{ analysis_id: string; status: string }> {
  const response = await aiApi.post('/api/ai/batch-analyze', params)
  return response.data
}

// --- Jobs panel ---

export type AnalysisListType = 'ai_batch' | 'semantic' | 'bertopic' | 'topic'
export type AnalysisStatus = 'pending' | 'running' | 'complete' | 'error' | 'cancelled'

export interface AnalysisListItem {
  analysis_id: string
  search_id?: string | null
  search_query?: string | null
  dataset_id?: string | null
  dataset_name?: string | null
  name: string
  analysis_type: string
  status: AnalysisStatus
  error_message?: string | null
  created_at: string
}

export async function listAnalyses(
  opts: { type?: AnalysisListType; status?: AnalysisStatus; limit?: number } = {}
): Promise<AnalysisListItem[]> {
  const response = await api.get('', { params: opts })
  return response.data
}

export async function deleteAnalysis(analysisId: string): Promise<{ message: string }> {
  const response = await api.delete(`/${analysisId}`)
  return response.data
}

export async function cancelAnalysis(analysisId: string): Promise<{ message: string }> {
  const response = await api.post(`/${analysisId}/cancel`)
  return response.data
}

export async function renameAnalysis(analysisId: string, name: string): Promise<AnalysisListItem> {
  const response = await api.patch(`/${analysisId}`, { name })
  return response.data
}
